import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  LiveKitRoom,
  PreJoin,
  RoomAudioRenderer,
  VideoConference,
  useConnectionState,
} from "@livekit/components-react";
import { ConnectionState } from "livekit-client";
import "@livekit/components-styles";
import { ArrowLeft, Loader2, ShieldCheck, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/auth";
import BtcLogo from "../../components/BtcLogo";

/**
 * Locale-safe formatter for LiveKit chat message timestamps.
 * Guards against odd navigator.language values (e.g. "en-US@posix" in some
 * containers/OSes) that make toLocaleTimeString throw a RangeError. If the
 * browser locale is invalid, we fall back to a plain ISO time string.
 */
function safeChatFormatter(message /* , idx, msgs */) {
  const text = message?.message ?? "";
  const ts = message?.timestamp;
  if (!ts) return text;
  try {
    const t = new Date(ts);
    // Try user's locale first; if invalid, drop to plain HH:mm.
    let stamp;
    try {
      stamp = t.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
    } catch (_e) {
      stamp = t.toISOString().slice(11, 16);
    }
    return `[${stamp}] ${text}`;
  } catch (_e) {
    return text;
  }
}

class RoomErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { crashed: false }; }
  static getDerivedStateFromError() { return { crashed: true }; }
  componentDidCatch(err) { /* eslint-disable no-console */ console.error("Room crashed", err); }
  render() {
    if (this.state.crashed) {
      return (
        <div className="flex-1 flex items-center justify-center p-6">
          <div data-testid="room-crash" className="max-w-md bg-white rounded-2xl p-8 text-center">
            <AlertCircle className="w-10 h-10 mx-auto text-red-500" />
            <h1 className="text-xl font-bold mt-3 text-[#0B132B]">Something went wrong in the call</h1>
            <p className="text-[#4B5563] mt-2">Please head back and rejoin from your sessions list.</p>
            <a href="/portal/sessions" className="mt-6 btc-btn-primary inline-flex">Back to sessions</a>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function SessionRoom() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [tokenBundle, setTokenBundle] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionMeta, setSessionMeta] = useState(null);
  const [preJoinChoices, setPreJoinChoices] = useState(null);
  // Phase 11 — mobile device flag; drives the orientation hint and the
  // extra "confirm before leaving" affordance. No behavioural change on
  // desktop.
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;

  const fetchToken = useCallback(async () => {
    setLoading(true);
    try {
      const [s, t] = await Promise.all([
        api.get(`/buddy/sessions/${sessionId}`),
        api.post(`/buddy/sessions/${sessionId}/video/token`, {}),
      ]);
      setSessionMeta(s.data.session);
      setTokenBundle(t.data);
      setError(null);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Cannot join this session.");
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => { fetchToken(); }, [fetchToken]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0B132B]">
        <Loader2 className="w-8 h-8 text-white animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0B132B] p-6">
        <div data-testid="room-error" className="max-w-md bg-white rounded-2xl p-8 text-center">
          <AlertCircle className="w-10 h-10 mx-auto text-red-500" />
          <h1 className="text-xl font-bold mt-3 text-[#0B132B]">Can't join right now</h1>
          <p className="text-[#4B5563] mt-2">{error}</p>
          <button
            data-testid="room-back-btn"
            onClick={() => navigate("/portal/sessions")}
            className="mt-6 btc-btn-primary"
          >
            Back to sessions
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B132B] flex flex-col" data-testid="session-room">
      <header className="flex items-center justify-between px-4 sm:px-8 py-3 bg-black/40 text-white">
        <div className="flex items-center gap-3">
          <BtcLogo className="h-8 w-auto" variant="white" />
          <span className="hidden sm:inline text-sm text-white/70">Buddy session</span>
        </div>
        <div className="text-xs uppercase tracking-wider text-white/60">
          {sessionMeta?.duration_label || ""}
        </div>
      </header>

      {!preJoinChoices ? (
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="w-full max-w-3xl bg-white rounded-3xl overflow-hidden shadow-2xl">
            <div className="px-6 sm:px-8 py-6 bg-gradient-to-br from-[#2D9B4E] to-[#1A5FAB] text-white">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5" />
                <div className="text-xs uppercase tracking-wider font-bold">Private &amp; not recorded</div>
              </div>
              <h1 className="text-2xl sm:text-3xl font-extrabold mt-2">You're about to join your Buddy</h1>
              <p className="text-white/85 mt-1 text-sm">
                Take a breath. Test your camera and mic below, then join when you're ready.
              </p>
            </div>
            <div className="p-4 sm:p-6" data-testid="prejoin-container">
              {isMobile && (
                <div
                  data-testid="mobile-session-hints"
                  className="mb-4 rounded-xl bg-[#F8F9FB] border border-gray-100 px-4 py-3 text-xs text-[#4B5563] leading-relaxed"
                >
                  <p className="font-semibold text-[#0B132B] mb-1">Before you join</p>
                  <ul className="list-disc pl-4 space-y-1">
                    <li>Allow camera and microphone when prompted. If you accidentally deny, tap the site permissions in your browser and re-allow.</li>
                    <li>Landscape orientation gives you the widest view of your Buddy.</li>
                    <li>Wi-Fi is more stable than mobile data for video.</li>
                  </ul>
                </div>
              )}
              <PreJoin
                defaults={{ videoEnabled: true, audioEnabled: true }}
                onSubmit={(choices) => setPreJoinChoices(choices)}
                onError={(e) =>
                  setError(
                    e?.name === "NotAllowedError"
                      ? "Camera or microphone access was blocked. Please re-allow permissions in your browser and try again."
                      : e?.message || "Device error"
                  )
                }
              />
            </div>
          </div>
        </div>
      ) : (
        <RoomErrorBoundary>
          <LiveKitRoom
            data-testid="livekit-room"
            token={tokenBundle.token}
            serverUrl={tokenBundle.url}
            connect={true}
            audio={preJoinChoices.audioEnabled}
            video={preJoinChoices.videoEnabled}
            options={{ adaptiveStream: true, dynacast: true }}
            onDisconnected={() => {
              // Phase 11 — on mobile browsers a stray tap can end the
              // session; a lightweight confirm avoids accidental exits
              // while preserving the exact server-side revocation
              // behaviour (`revoke_room` runs regardless on end).
              if (isMobile) {
                // eslint-disable-next-line no-alert
                const confirmed = window.confirm(
                  "Leave your Buddy session? You can rejoin from the sessions list if it is still active."
                );
                if (!confirmed) return;
              }
              toast("Your session has ended.");
              navigate("/portal/sessions");
            }}
            onError={(e) => setError(e?.message || "Room error")}
            style={{ height: "calc(100vh - 56px)" }}
          >
            <RoomStateBanner />
            <VideoConference chatMessageFormatter={safeChatFormatter} />
            <RoomAudioRenderer />
          </LiveKitRoom>
        </RoomErrorBoundary>
      )}
    </div>
  );
}

function RoomStateBanner() {
  const state = useConnectionState();
  if (state === ConnectionState.Connected) return null;
  return (
    <div
      data-testid="room-state-banner"
      className="absolute top-16 left-1/2 -translate-x-1/2 z-50 bg-black/70 text-white text-xs px-3 py-1.5 rounded-full"
    >
      {state === ConnectionState.Reconnecting ? "Reconnecting…" :
       state === ConnectionState.Connecting ? "Connecting…" :
       `Connection: ${state}`}
    </div>
  );
}
